<template>
  <div class="account-setting">
    <div class="flex items-start">
      <div class="flex flex-col items-center">
        <Avatar
          :size="80"
          :url="previewFile(orgStore.currentOrganization?.logo)"
          :name="orgStore.currentOrganization?.organizationName"
        />
        <a-button @click="selectAvatar" type="text" class="mt-4 mx-auto text-center">编辑</a-button>
      </div>
      <div class="flex-1 pl-[32px]">
        <SingleEditPForm
          ref="formRef"
          class="tow-line-form"
          :form-items="basicFormItems"
          :form-data="organization"
          @change="handleChange"
        >
          <template #code>
            <a-button type="text" class="copy-btn" @click.stop="copyOrgCode">
              <CopyOutlined />
            </a-button>
          </template>
          <template #bottomorganizationName>
            <a-button @click="formRef.editByKey('organizationName')" type="link">修改</a-button>
          </template>
          <template #bottomtransfer>
            <a-button @click="transferVisible = true" type="link">立即移交</a-button>
          </template>
          <template #bottomdeleteOrg>
            <a-button @click="deleteVisible = true" type="link">解散团队</a-button>
          </template>
        </SingleEditPForm>
      </div>
    </div>
  </div>
  <Modal title="移交团队" v-model:visible="transferVisible" @ok="transferOwner">
    <div>移交团队后，你在团队中的权限将默认修改为可管理。</div>
    <div style="margin-top: 8px">
      选择移交对象<a-select
        style="margin-left: 8px; width: 140px"
        size="small"
        placeholder="请选择移交对象"
        :fieldNames="{ value: 'userId', label: 'employeeName' }"
        :options="employeeList"
        v-model:value="owner"
      ></a-select>
    </div>
  </Modal>
  <Modal title="解散团队" v-model:visible="deleteVisible" :btns="deleteModalBtns">
    <div>
      确定要解散[<span class="delete-org-name">{{ organization.organizationName }}</span
      >]吗？
    </div>
    <div class="my-2">解散后将永久删除所有团队文件 操作不可撤销，请务必慎重操作。</div>
    <div>
      <a-input size="small" placeholder="请输入团队名称" v-model:value="verificationName"></a-input>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, nextTick } from 'vue'
import { Avatar, Modal } from '@elecmap/elecmap-ui'
import { message } from 'ant-design-vue'
import type { IModalBtnItem } from '@elecmap/elecmap-ui/lib/modal/types'
import { previewFile } from '@elecmap/api/src/cad/file'
import SingleEditPForm from '@elecmap/shared/src/components/form/SingleEditPForm.vue'
import type { PFormItemProps } from '@elecmap/shared/src/components/form/SingleEditPForm.vue'
import { CopyOutlined } from '@ant-design/icons-vue'
import {
  queryCurrentOrganization,
  updateOrganizationLogo,
  updateOrganizationName,
  transferOrganizationOwner,
  dissolveOrganization,
  listEmployeeByOrganizationId,
  listOrganization,
} from '@elecmap/api/src/ram'
import { useOrganization } from '@/store/organization'
import { useSpaceStore } from '@/store/space'
import { selectFile } from '@elecmap/shared/src/utils/dom'
import userStore from '@/store/user'
import { getHttpErrorMessage } from '@/utils/http-error-message'

interface FileItem {
  uid: string
  name?: string
  status?: string
  response?: any
  url?: string
  type?: string
  size: number
  originFileObj: any
}

const emit = defineEmits(['close'])

const formRef = ref()
const orgStore = useOrganization()
const organization = ref<any>({})
const isOrgOwner = computed(() => {
  const uid = userStore().userInfo?.userId
  const ownerId = organization.value?.owner
  if (uid == null || ownerId == null) return false
  return Number(uid) === Number(ownerId)
})
const employeeList = ref<any[]>([])
const transferVisible = ref(false)
const deleteVisible = ref(false)
const owner = ref()
const verificationName = ref<string>()

async function copyOrgCode() {
  const code = organization.value?.code
  if (!code) {
    message.warning('暂无可复制的团队代码')
    return
  }
  try {
    await navigator.clipboard.writeText(String(code))
    message.success('团队代码已复制')
  } catch (e) {
    message.error('复制失败，请手动复制')
  }
}

async function transferOwner() {
  if (!owner.value) return
  try {
    await transferOrganizationOwner(
      {
        organizationId: orgStore.currentOrganization!.id,
        userId: owner.value,
      },
      { hideToast: true },
    )
    userStore().tryGetRole()
    transferVisible.value = false
  } catch (e) {
    message.error(getHttpErrorMessage(e, '移交失败'))
  }
}

const deleteModalBtns = computed<IModalBtnItem[]>(() => [
  {
    text: '解散团队',
    type: 'primary',
    onClick: () => {
      dissolveOrganization({ hideToast: true })
        .then(data => {
          orgStore.setCurrentOrganizationId(data)
          const ss = useSpaceStore()
          ss.navs = []
          ss.currentSpace = undefined
          ss.orgSpaceList = []
          if (data) {
            window.location.href = '/#/workbench'
            nextTick(() => {
              window.location.reload()
            })
          } else {
            emit('close')
          }
          orgStore.setOrganization(
            orgStore.organization.filter(o => o.id !== orgStore.currentOrganization!.id),
          )
        })
        .catch(e => {
          message.error(getHttpErrorMessage(e, '解散失败'))
        })
    },
    disabled: verificationName.value !== organization.value.organizationName,
  },
  {
    text: '取消',
    onClick: () => {
      deleteVisible.value = false
    },
  },
])

const baseFormItems: PFormItemProps[] = [
  {
    key: 'organizationName',
    label: '团队名称',
    type: 'Input',
  },
  {
    key: 'code',
    label: '团队代码',
    type: 'Text',
  },
  {
    key: 'creatorName',
    label: '创建者',
    type: 'Text',
  },
  {
    key: 'createdTime',
    label: '创建日期',
    type: 'Text',
  },
]

const ownerOnlyFormItems: PFormItemProps[] = [
  {
    key: 'transfer',
    label: '移交团队',
    type: 'Text',
    info: '团队创建者可将团队移交给其他成员，移交后将不再保留团队的管理权限。',
  },
  {
    key: 'deleteOrg',
    label: '解散团队',
    type: 'Text',
    info: '解散团队后，团队文件将永久删除，所有成员将无法访问团队文件。',
  },
]

const basicFormItems = computed<PFormItemProps[]>(() =>
  isOrgOwner.value ? [...baseFormItems, ...ownerOnlyFormItems] : [...baseFormItems],
)

async function selectAvatar() {
  const f = await selectFile()
  await updateOrganizationLogo({ logo: f[0].uuid })
  organization.value.logo = f[0].uuid
  useOrganization().currentOrganization.logo = organization.value.logo
}

async function getOrganization() {
  organization.value = await queryCurrentOrganization()
}

async function handleChange(key: string, value: any) {
  switch (key) {
    case 'organizationName':
      const org = await updateOrganizationName({ name: value })
      listOrganization().then(orgs => {
        useOrganization().setOrganization(orgs)
      })
      organization.value = org
      break
  }
}
onMounted(() => {
  getOrganization()
  listEmployeeByOrganizationId({
    organizationId: orgStore.currentOrganization!.id,
  }).then(data => {
    employeeList.value = data.filter(d => d.userId !== userStore().userInfo?.userId)
  })
})
</script>

<style lang="less" scoped>
.account-setting {
  :deep(.em-user-avatar) {
    overflow: hidden;
  }

  :deep(.single-suffix) {
    display: block;
  }

  .tow-line-form {
    :deep(.single-edit-trigger.is-text .single-suffix) {
      display: block;
    }
    :deep(.single-edit-trigger:not(.is-text):hover) {
      outline: none;
    }
    .copy-btn {
      padding: 0;
      margin-left: 6px;
      height: 20px;
      color: rgba(0, 0, 0, 0.45);
      &:hover {
        color: rgba(0, 0, 0, 0.88);
      }
    }
    :deep(.ant-form-item) {
      flex-direction: column;
      margin-bottom: 24px;
      .ant-form-item-label {
        width: 100%;
        max-width: 100%;
        flex: none;
        display: block;
        margin-bottom: 12px;
        > label {
          font-size: 14px;
          font-weight: 450;
        }
      }
      .ant-form-item-control {
        flex: none;
      }
      .single-edit-trigger {
        width: auto;
        min-width: 30px;
      }
    }
    :deep(.form-item-bottom) {
      transform: translate(-6px, -24px);
    }
  }
}
</style>
